import {stringify, parse} from 'query-string';
import {
  ISSUE_FILTER_PROPERTIES,
  TEMPLATES_FILTER_PROPERTIES,
  SORTING_PARAMS,
  PAGE_PARAMS,
  BOOLEAN_QUERY_PARAMS,
  ALL_MULTI_SELECT_FILTERS
} from '../constants/issue-query-params';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import isArray from 'lodash/isArray';

export const parseQueryString = parse;

export const stringifyQuery = query => stringify(query, {arrayFormat: 'brackets'});

const URL_PARAMS_BLACKLIST = [PAGE_PARAMS.OFFSET];

/*
 * Search related helpers
 */
const WILD_CARD_OPERAND = '%';
const operands = ['=', '!', '\\.\\.\\.', ',', 'null', WILD_CARD_OPERAND, '~'];

const createRegexFromOperandsArray = operandsArray => {
  const concatOperands = operandsArray.join('|');
  return new RegExp(concatOperands, 'gi');
};

const operandsRegex = createRegexFromOperandsArray(operands);

const basicQueryTransformer = (value, operandToIgnore) => {
  if (value === null) {
    return '\\null';
  }
  if (typeof value !== 'string') {
    return value;
  }
  const currentOperandRegex = operandToIgnore
    ? createRegexFromOperandsArray(operands.filter(operand => operand !== operandToIgnore))
    : operandsRegex;

  return value.replace(currentOperandRegex, matched => `\\${matched}`).trim();
};

export const createSearchQuery = searchExpression =>
  `${WILD_CARD_OPERAND}${basicQueryTransformer(searchExpression)}${WILD_CARD_OPERAND}`;

export const stripSearchQuery = searchExpression => {
  if (!searchExpression) {
    return '';
  }

  const lastCharIndex = searchExpression.length - 1;
  const sliceBeginIndex = searchExpression[0] === WILD_CARD_OPERAND ? 1 : 0;
  const sliceEndIndex = searchExpression[lastCharIndex] === WILD_CARD_OPERAND ? lastCharIndex : undefined;
  return searchExpression.slice(sliceBeginIndex, sliceEndIndex);
};

const parseParams = queryParams => {
  const updatedParams = Object.assign({}, queryParams);
  BOOLEAN_QUERY_PARAMS.forEach(param => {
    if (updatedParams[param]) {
      updatedParams[param] = updatedParams[param].toLowerCase() === 'true';
    }
  });

  //If the user select only one options in a multi select it will get return as a string and not an array
  ALL_MULTI_SELECT_FILTERS.forEach(param => {
    if (updatedParams[param]) {
      updatedParams[param] = isArray(updatedParams[param]) ? updatedParams[param] : [updatedParams[param]];
    }
  });

  return updatedParams;
};

const cleanupQueryParams = queryParams => {
  const updatedParams = Object.assign({}, queryParams);
  URL_PARAMS_BLACKLIST.forEach(param => delete updatedParams[param]);

  const keys = Object.keys(updatedParams);
  keys.forEach(param => {
    if (
      isNullOrUndefined(updatedParams[param]) ||
      (!updatedParams[param].length && typeof updatedParams[param] !== 'boolean')
    ) {
      delete updatedParams[param];
    }
  });

  return updatedParams;
};

export const updateUrlWithQueryParams = queryParams => {
  const currentParams = parseQueryString(window.location.search);
  const updatedParams = cleanupQueryParams(Object.assign({}, currentParams, queryParams));
  let updateResult = {hasBeenUpdated: false};

  // Stripping the wild cards so they won't be in the query param url
  if (updatedParams[ISSUE_FILTER_PROPERTIES.SEARCH]) {
    updatedParams[ISSUE_FILTER_PROPERTIES.SEARCH] = stripSearchQuery(updatedParams[ISSUE_FILTER_PROPERTIES.SEARCH]);
  }

  let queryParamsString = stringifyQuery(updatedParams);
  const pathname = window.location.pathname;

  // Preventing push to history when no change is needed. Comparison is valid since stringifyQuery sorts the parameters
  // We're parsing and stringifying the window.location.search since the consuming app could've changed the order of the query parameters
  if (queryParamsString !== stringifyQuery(currentParams)) {
    queryParamsString = queryParamsString.length ? `?${queryParamsString}` : '';
    const hash = window.location.hash ? window.location.hash : '';
    window.history.pushState({pathname, queryParamsString}, '', `${pathname}${queryParamsString}${hash}`);
    updateResult = {pathname, queryParamsString, hasBeenUpdated: true};
  }

  return updateResult;
};

/**
 * Returns Issues-UI relevant queryParams object.
 * If no relevant params found - returns empty object
 */
export const getIssuesUIQueryParamsFromUrl = (urlParams = window.location.search) => {
  const relevantParams = {};

  if (urlParams.length) {
    const params = parseQueryString(urlParams);
    const keysToCheck = [].concat(
      Object.values(ISSUE_FILTER_PROPERTIES),
      Object.values(TEMPLATES_FILTER_PROPERTIES),
      Object.values(SORTING_PARAMS)
    );

    keysToCheck.forEach(key => {
      if (params[key]) {
        relevantParams[key] = params[key];
      }
    });
  }

  // Stripping the wild cards so they won't be in the query param url
  if (relevantParams[ISSUE_FILTER_PROPERTIES.SEARCH]) {
    relevantParams[ISSUE_FILTER_PROPERTIES.SEARCH] = createSearchQuery(relevantParams[ISSUE_FILTER_PROPERTIES.SEARCH]);
  }

  return parseParams(relevantParams);
};

export const getLocationObjectFromUrl = () => {
  const pathname = window.location.pathname;
  const queryParamsString = stringifyQuery(getIssuesUIQueryParamsFromUrl());
  return {pathname, queryParamsString};
};

/**
 * Determines if the Issues-UI query params have changed.
 * @returns {boolean}
 */
export const hasIssueUIParamsChanged = (previousLocation, newLocation) => {
  // If previousLocation is empty it means that we've just loaded issues-ui and we should return true
  if (!previousLocation) {
    return true;
  } else if (newLocation.pathname !== previousLocation.pathname) {
    // If pathname is different than there has been a routing change and the app/package should handle in the components level
    return false;
  } else {
    const newIssuesUIParams = getIssuesUIQueryParamsFromUrl(newLocation.queryParamsString);
    const lastIssuesUIParams = getIssuesUIQueryParamsFromUrl(previousLocation.queryParamsString);
    return stringifyQuery(newIssuesUIParams) !== stringifyQuery(lastIssuesUIParams);
  }
};
